import React from 'react';
import T from 'prop-types';
// components
import { FormattedMessage } from 'react-intl';
import AsyncSelectLoadMore from 'components/AsyncSelect/AsyncSelectLoadMore';
import Input from 'components/Input';
import CheckboxMenu from './CheckboxMenu';
// utils
import classnames from 'classnames/bind';
import isEmpty from 'lodash.isempty';
import pick from 'lodash.pick';
// endpoints
import { searchFarms } from 'endpoints/farms';
import { searchCompanies } from 'endpoints/companies';
import { searchUsers } from 'endpoints/users';
import { searchPigGroups } from 'endpoints/pigGroups';
// filter constants
import {
  checkboxProps,
  entityNameSections,
  sectionInputPlaceholderIds,
  userFilterSectionTypes,
} from 'utils/constants/userFilters';
// styles
import styles from './Menu.module.scss';

const cn = classnames.bind(styles);

const farmsGetter = (params) => searchFarms(params);
const companiesGetter = (params) => searchCompanies(params);
const pigGroupsGetter = (params) => searchPigGroups(params);
const usersGetter = (params) => searchUsers({ ...params, with_current_user: true });

const userOptionKeys = { value: 'id', label: 'full_name' };
const defaultOptionKeys = { value: 'id', label: 'name' };

const EntityNameMenu = ({
  type,
  currentValue,
  onInputChange,
  inputValue,
  onSelect,
  selectedEntity,
  ...others
}) => (
  <CheckboxMenu {...pick(others, checkboxProps)} currentValue={currentValue}>
    {(value) => {
      const isValueSelected = value === currentValue;
      const showAsyncSelect = isValueSelected && ['only', 'except'].includes(value);
      return (
        <>
          {showAsyncSelect && (type === userFilterSectionTypes.farm) && (
            <AsyncSelectLoadMore
              className={cn('value-async-select')}
              loadMoreMode="auto"
              selected={!isEmpty(selectedEntity) ? { value: selectedEntity.id, label: selectedEntity.name } : null}
              onChange={onSelect}
              optionsGetter={farmsGetter}
              optionsKeys={defaultOptionKeys}
              placeholder={<FormattedMessage id="general.farmName" />}
            />
          )}
          {showAsyncSelect &&
            [userFilterSectionTypes.company, userFilterSectionTypes.managementCompany].includes(type) && (
              <AsyncSelectLoadMore
                className={cn('value-async-select')}
                loadMoreMode="auto"
                selected={!isEmpty(selectedEntity) ? { value: selectedEntity.id, label: selectedEntity.name } : null}
                onChange={onSelect}
                optionsGetter={companiesGetter}
                optionsKeys={defaultOptionKeys}
                placeholder={<FormattedMessage id="general.companyName" />}
              />
          )}
          {showAsyncSelect &&
            ([
              userFilterSectionTypes.user,
              userFilterSectionTypes.dcNoteMention,
              userFilterSectionTypes.latestSvrCreatedBy,
            ].includes(type)) && (
              <AsyncSelectLoadMore
                className={cn('value-async-select')}
                loadMoreMode="auto"
                selected={!isEmpty(selectedEntity) ?
                  { value: selectedEntity.id, label: selectedEntity.full_name } :
                  null
                }
                optionsGetter={usersGetter}
                optionsKeys={userOptionKeys}
                onChange={onSelect}
                placeholder={<FormattedMessage id="general.userName" />}
              />
          )}
          {showAsyncSelect && (type === userFilterSectionTypes.pigGroup) && (
            <AsyncSelectLoadMore
              className={cn('value-async-select')}
              loadMoreMode="auto"
              selected={!isEmpty(selectedEntity) ?
                { value: selectedEntity.id, label: selectedEntity.name }
                : null}
              optionsGetter={pigGroupsGetter}
              optionsKeys={defaultOptionKeys}
              onChange={onSelect}
              placeholder={<FormattedMessage id="general.groupId" />}
            />
          )}
          {isValueSelected && !['only', 'except'].includes(value) && (
            <div className={cn('value-input-line')}>
              <FormattedMessage id={sectionInputPlaceholderIds[type]}>
                {(placeholder) => (
                  <Input
                    placeholder={placeholder}
                    className={cn('value-input', 'longer')}
                    value={inputValue}
                    onChange={onInputChange}
                  />
                )}
              </FormattedMessage>
            </div>
          )}
        </>
      );
    }}
  </CheckboxMenu>
);

EntityNameMenu.propTypes = {
  type: T.oneOf(entityNameSections).isRequired,
  isOpened: T.bool.isRequired,
  currentValue: T.string,
  options: T.array.isRequired,
  onConditionChange: T.func.isRequired,
  onInputChange: T.func.isRequired,
  inputValue: T.oneOfType([T.number, T.string]),
  selectedEntity: T.object,
  onSelect: T.func.isRequired,
  onApply: T.func.isRequired,
};

export default EntityNameMenu;
